import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import trainerPoolImage from '@/assets/trainer-pool.jpg';

export default function Story() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="story-section"
      className="py-24 md:py-36 px-4 bg-white relative overflow-hidden"
      ref={ref}
    >
      {/* Subtle background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-primary/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

      <div className="max-w-6xl mx-auto relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Image with decorative frame */}
          <motion.div
            className="order-2 lg:order-1 relative"
            initial={{ opacity: 0, x: -60 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Decorative elements behind image */}
            <div className="absolute -top-4 -left-4 w-full h-full bg-gradient-to-br from-primary/20 to-primary/5 rounded-2xl" />
            <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-primary/30 rounded-2xl" />

            {/* Main image */}
            <img
              src={trainerPoolImage}
              alt="Lucie Kubešová Leišová"
              className="relative rounded-2xl w-full object-cover shadow-2xl z-10"
            />

            {/* Floating badge */}
            <motion.div
              className="absolute -bottom-6 -right-6 bg-white rounded-xl p-4 shadow-xl z-20"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="text-center">
                <span className="text-4xl font-bold text-primary">5+</span>
                <p className="text-sm text-foreground/70 font-medium">let zkušeností</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Content */}
          <motion.div
            className="order-1 lg:order-2"
            initial={{ opacity: 0, x: 60 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            <motion.span
              className="inline-block text-primary font-semibold text-sm uppercase tracking-wider mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              Náš příběh
            </motion.span>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-8 text-foreground leading-tight">
              Jak Swimmie vzniklo
            </h2>

            <div className="space-y-5 text-base md:text-lg leading-relaxed text-foreground/80">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-justify"
              >
                Za 5 let mé trenérské kariéry mi rukama prošly stovky klientů. Všechny tyto lidi
                spojovalo odhodlání a láska k vodě, ale také jedna věc, která tak pozitivní nebyla:
                V&nbsp;záplavě všemožných volně dostupných videí a materiálů pro ně bylo velmi
                těžké vytvořit si tréninkový plán, který by jim přinášel chtěné výsledky.
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="text-justify"
              >
                Dlouhé měsíce jsem přemýšlela, jak těmto lidem efektivně pomoci k&nbsp;tomu, aby si
                mohli plnit své plavecké sny. S&nbsp;tímto cílem vzniklo Swimmie –
                <span className="text-primary font-semibold"> první aplikace svého druhu na českém trhu</span>,
                která je vaším tréninkovým plánem v kapse.
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="text-justify"
              >
                Aplikace vám sestaví tréninkový plán na míru, poskytne videokurz na techniku kraulu
                a možnost poslat video k&nbsp;ohodnocení profesionálním trenérem. Součástí je také
                tréninkový deník pro sledování pokroku a sdílení úspěchů na sociálních sítích.
              </motion.p>
            </div>

            {/* Author signature */}
            <motion.div
              className="mt-8 pt-6 border-t border-gray-200"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.7 }}
            >
              <p className="text-foreground font-semibold">Lucie Kubešová Leišová</p>
              <p className="text-foreground/60 text-sm">Zakladatelka Swimmie</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
